/* eslint-disable no-unused-vars */
import React, { useCallback, useContext, useRef, useMemo, useEffect } from 'react';
import { MenuItem, Checkbox, Search, CustomButton, Loader } from 'components';
import { ManageCampaignsContext } from 'contexts';
import { AllCampaignsStatusIcon, AllButArchived, FieldOnly } from 'assets/icons';
import * as S from './FilterBtn_v2.styled';
import { CountryItem } from './CountryItem';
import { useSignal } from '@preact/signals-react';
import { manageAppsAPI } from 'services/manageApps.service';
import { campaignStatuses } from 'constants';
import { getLocalStorageItem, saveLocalStorageItem } from 'utils';

const chooseIcon = {
  allStatuses: AllCampaignsStatusIcon,
  allButArchived: AllButArchived,
  active: FieldOnly,
  paused: FieldOnly,
  suspended: FieldOnly,
};

const CampaignStatus = ({ isShow, selectedStatusFilter$ }) => {
  const isStatusFilterSelected = useCallback(
    (fieldName) => {
      return fieldName === selectedStatusFilter$.value;
    },
    [selectedStatusFilter$.value],
  );

  return (
    <S.FilterContentContainer isShow={isShow}>
      {Object.entries(campaignStatuses).map(([fieldName, title], index) => {
        const Icon = chooseIcon[fieldName];
        return (
          <MenuItem
            isSelected={isStatusFilterSelected(fieldName)}
            height={40}
            value={fieldName}
            key={index}
            onClick={() => {
              selectedStatusFilter$.value = fieldName;
              saveLocalStorageItem({
                key: 'tableState',
                field: 'statusFilter',
                value: fieldName,
              });
            }}
          >
            <S.Label fieldName={fieldName}>
              <Icon />
              {title}
            </S.Label>
          </MenuItem>
        );
      })}
    </S.FilterContentContainer>
  );
};

const TargetedGeos = ({ isShow, isOpen$, menuClick }) => {
  const { isError, isLoading } = manageAppsAPI.useGetCountriesListQuery(undefined, {
    skip: isShow === false,
  });

  const { fullNameCountries, selectedCountriesFilter$, handleApplyClick } =
    useContext(ManageCampaignsContext);

  const searchRef = useRef(null);
  const countrySearch$ = useSignal('');

  useEffect(() => {
    if (isShow) {
      selectedCountries$.value = [...selectedCountriesFilter$.value];
    } else {
      selectedCountries$.value = [];
    }
  }, [isShow]);

  const selectedCountries$ = useSignal(selectedCountriesFilter$.value);

  const handleClick = useCallback(
    (isChecked, cca2) => {
      if (isChecked) {
        selectedCountries$.value = [...selectedCountries$.value, cca2];
      } else {
        selectedCountries$.value = [...selectedCountries$.value.filter((name) => name !== cca2)];
      }
    },
    [selectedCountries$.value],
  );

  const handleApplyClickAction = useCallback(() => {
    isOpen$.value = false;
    handleApplyClick(selectedCountries$.value);
    menuClick('campaignStatus', 228);
  }, [selectedCountries$.value]);

  const handleReset = useCallback(() => {
    isOpen$.value = false;
    menuClick('campaignStatus', 228);
  }, [selectedCountries$.value]);

  const filteredCountries = useMemo(() => {
    if (!countrySearch$.value) return fullNameCountries;
    return fullNameCountries.filter(({ country, cca2 }) => {
      if (country?.toLowerCase().includes(countrySearch$.value.toLowerCase())) return true;
      if (cca2?.toLowerCase().includes(countrySearch$.value.toLowerCase())) return true;
    });
  }, [countrySearch$.value, fullNameCountries]);

  if (isLoading && isShow)
    return (
      <S.CenteredLoader>
        <Loader.Local delay={0} />
      </S.CenteredLoader>
    );
  if (isError && isShow) return <p>Something went wrong</p>;
  if (!filteredCountries?.length && isShow)
    return <p style={{ textAlign: 'center', width: '100%', marginTop: '20px' }}>No countries</p>;
  return (
    <S.TargetContainer isShow={isShow}>
      <S.SearchContainer>
        <Search
          ref={searchRef}
          placeholder='Search Country'
          value={countrySearch$.value}
          onChange={(e) => {
            countrySearch$.value = e.target.value;
          }}
          rightIconOnClick={() => {
            countrySearch$.value = '';
          }}
        />
      </S.SearchContainer>
      <S.CountriesContent>
        {filteredCountries?.map(({ country, cca2 }, index) => {
          return (
            <S.CountryItemBox key={cca2}>
              <Checkbox
                id={cca2}
                name={cca2}
                onChange={(isChecked) => handleClick(isChecked, cca2)}
                isChecked={selectedCountries$.value.includes(cca2)}
                label={<CountryItem country={country} cca2={cca2} search$={countrySearch$} />}
              />
            </S.CountryItemBox>
          );
        })}
      </S.CountriesContent>
      <S.Footer>
        <CustomButton
          id='add_more_event'
          buttonColor={CustomButton.Colors.Secondary}
          onClick={handleReset}
          square
          containerStyle={{ marginLeft: 'auto' }}
          btnWrapperStyle={{ maxHeight: '32px' }}
          //disabled={true}
        >
          Reset
        </CustomButton>
        <CustomButton
          square
          id='add_more_event'
          btnWrapperStyle={{ maxHeight: '32px' }}
          buttonColor={CustomButton.Colors.Primary}
          onClick={handleApplyClickAction}
          //disabled={true}
        >
          Apply
        </CustomButton>
      </S.Footer>
    </S.TargetContainer>
  );
};

export const FilterContent = {
  TargetedGeos,
  CampaignStatus,
};
